/**
 * Blogartikel — publiek; SEO + JSON-LD BlogPosting.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Calendar, Clock, Tag } from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';
import { canonicalUrl, DEFAULT_OG_IMAGE, SITE_ORIGIN } from '../lib/site-url';

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(iso)
    );
  } catch {
    return iso;
  }
}

/** Basisopschoning CMS-HTML (superadmin-vertrouwd; geen volledige sanitizer). */
function sanitizeBlogHtml(html: string): string {
  return html
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe\b[\s\S]*?<\/iframe>/gi, '')
    .replace(/<object\b[\s\S]*?<\/object>/gi, '')
    .replace(/<embed\b[^>]*>/gi, '')
    .replace(/\son\w+\s*=/gi, ' data-removed=');
}

export function BlogPost() {
  const { slug: rawSlug } = useParams<{ slug: string }>();
  const slug = rawSlug ? decodeURIComponent(rawSlug) : '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: () => api.getPublicBlogPost(slug),
    enabled: !!slug,
  });

  const post = data?.post;
  const errMsg = error instanceof Error ? error.message : '';
  const path = `/blog/${encodeURIComponent(slug)}`;
  const tags =
    post?.keywords
      ?.split(',')
      .map((t) => t.trim())
      .filter(Boolean)
      .slice(0, 20) ?? [];

  const jsonLd = post
    ? {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.meta_description || post.excerpt,
        datePublished: post.published_at,
        dateModified: post.updated_at || post.published_at,
        author: { '@type': 'Organization', name: post.author || 'CalqTool' },
        publisher: {
          '@type': 'Organization',
          name: 'CalqTool™',
          logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl(path) },
        keywords: post.keywords,
        articleSection: post.category,
        inLanguage: 'nl-NL',
        isPartOf: { '@type': 'Blog', name: 'CalqTool blog', url: `${SITE_ORIGIN}/blog` },
      }
    : undefined;

  if (!slug) {
    return (
      <div>
        <SeoMeta path="/blog" title="Blog" />
        <p>Ongeldige URL.</p>
      </div>
    );
  }

  return (
    <div className="animate-page-enter">
      {post ? (
        <SeoMeta
          path={path}
          title={post.meta_title}
          description={post.meta_description}
          keywords={post.keywords}
          ogType="article"
          article={{
            publishedTime: post.published_at,
            modifiedTime: post.updated_at,
            section: post.category,
            author: post.author,
            tags: tags.length ? tags : undefined,
          }}
          jsonLd={jsonLd}
        />
      ) : (
        <SeoMeta path={path} title="Blogartikel" noindex description="Artikel laden of niet gevonden." />
      )}

      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Blog', href: '/blog' },
          { label: post?.title ?? 'Artikel' },
        ]}
      />

      {isLoading ? (
        <p className="text-text-muted">Laden…</p>
      ) : error || !post ? (
        <div className="rounded-xl border border-border bg-surface-card p-6">
          <p className="text-text-muted" role="alert">
            {errMsg || 'Dit artikel is niet (meer) beschikbaar.'}
          </p>
          <Link to="/blog" className="mt-4 inline-block font-medium text-primary hover:underline">
            ← Terug naar blog
          </Link>
        </div>
      ) : (
        <article>
          <header className="mb-8 border-b border-border pb-6">
            <p className="mb-2 text-sm font-medium text-primary">{post.category}</p>
            <h1 className="text-3xl font-bold tracking-tight text-text sm:text-4xl">{post.title}</h1>
            <p className="mt-3 max-w-3xl text-lg text-text-muted">{post.excerpt}</p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-text-muted">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-4 w-4 shrink-0" aria-hidden />
                <time dateTime={post.published_at}>Gepubliceerd {fmtDate(post.published_at)}</time>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-4 w-4 shrink-0" aria-hidden />
                {post.reading_minutes} min lezen
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Tag className="h-4 w-4 shrink-0" aria-hidden />
                {post.author}
              </span>
            </div>
          </header>

          <div
            className="blog-article max-w-3xl"
            dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(post.body_html) }}
          />

          <nav className="mt-12 border-t border-border pt-6 text-sm text-text-muted" aria-label="Verder lezen">
            <p>
              <Link to="/blog" className="font-medium text-primary hover:underline">
                ← Alle artikelen
              </Link>
              {' · '}
              <Link to="/prijzen" className="font-medium text-primary hover:underline">
                Pakketten
              </Link>
              {' · '}
              <Link to="/contact" className="font-medium text-primary hover:underline">
                Contact
              </Link>
            </p>
          </nav>
        </article>
      )}
    </div>
  );
}
